const OPEN_STATUSES = new Set(["queued", "starting", "waiting_for_user", "running"])
const TERMINAL_STATUSES = new Set(["succeeded", "failed", "timed_out", "cancelled"])
const EFFORT_LABELS = {
  low: "Low",
  medium: "Medium",
  high: "High",
  xhigh: "Extra high",
  max: "Max",
  ultra: "Ultra (automatic subagents)"
}

function statusLabel(status) {
  return String(status || "unknown")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function setupPhase(setup) {
  if (setup.phase) return setup.phase
  if (setup.authorization_code_error) return "action_required"
  if (setup.authorization_code_stalled) return "needs_attention"
  if (setup.authorization_code_consumed_at) return "verifying"
  if (setup.authorization_code_submitted_at) return "sending"
  if (setup.authorization_uri) return "action_required"
  if (setup.status === "queued" || setup.status === "starting") return "starting"
  if (setup.status === "succeeded") return "connected"
  if (TERMINAL_STATUSES.has(setup.status)) return "failed"
  return "waiting"
}

function setupAuthorizationCopy(setup) {
  if (setup.requires_authorization_code) return "Open Claude login and approve access, then paste the code it shows."
  if (setup.user_code) return "Open the provider login and enter the user code shown below."
  return "Open this link to finish the provider login in your browser."
}

export function setupViewState(setup, providerName = statusLabel(setup?.provider)) {
  setup ||= {}
  switch (setupPhase(setup || {})) {
    case "action_required":
      if (setup.authorization_code_error) {
        return {
          title: "Claude Code did not accept that code",
          copy: setup.authorization_code_error,
          statusLabel: setup.status_label || "Action required"
        }
      }
      return {
        title: `Finish ${providerName} login`,
        copy: setup.requires_authorization_code
          ? "Open the login link, approve access, and paste the code it shows into the form below."
          : "Open the login link and enter the user code shown below. We detect the login automatically — no need to come back and confirm.",
        statusLabel: setup.status_label || "Action required"
      }
    case "needs_attention":
      return {
        title: "Claude Code has not finished signing in",
        copy: "The code may be incorrect or the provider login may be stuck. Copy a new code from the Claude page and try again, or abandon this login.",
        statusLabel: setup.status_label || "Needs attention"
      }
    case "verifying":
      return {
        title: "Claude Code is verifying your code",
        copy: "No action is needed. Successful logins usually finish within a few seconds.",
        statusLabel: setup.status_label || "Verifying code"
      }
    case "sending":
      return {
        title: "Sending your code",
        copy: "Conceptive received the code and is sending it to the login runner.",
        statusLabel: setup.status_label || "Sending code"
      }
    case "starting":
      return {
        title: "Starting secure login",
        copy: "Booting a private, one-time login runner just for you. This can take up to 5 minutes — hang tight, no action is needed yet.",
        statusLabel: setup.status_label || "Starting"
      }
    case "connected":
      return { title: `${providerName} connected`, copy: "The provider login completed and the credential is ready to use.", statusLabel: setup.status_label || "Connected" }
    case "failed":
      return {
        title: `${providerName} setup failed`,
        copy: setup.failure_message || setup.failure_code || "The setup attempt did not complete. No credential was kept for this failed attempt.",
        statusLabel: setup.status_label || statusLabel(setup.status)
      }
    default:
      return {
        title: "Preparing your login link",
        copy: "The runner is starting the provider login. Your login link will appear here automatically — no action needed yet.",
        statusLabel: setup.status_label || "Waiting"
      }
  }
}

export function setupStepStates(setup, provider) {
  switch (setupPhase(setup || {})) {
    case "connected":
      return { open: "done", authorize: "done", verify: "done" }
    case "action_required":
    case "needs_attention":
      return { open: "done", authorize: "active", verify: "upcoming" }
    case "sending":
    case "verifying":
      return { open: "done", authorize: "done", verify: "active" }
    case "failed":
      return { open: "upcoming", authorize: "upcoming", verify: "upcoming" }
    default:
      // Runner is still booting or the login link is not ready yet — the user
      // cannot open anything, so no user step is active.
      return { open: "upcoming", authorize: "upcoming", verify: "upcoming" }
  }
}

export function authorizationCodeValidationMessage(value) {
  const code = String(value || "").trim()
  if (/^https?:\/\//i.test(code)) {
    return "That is the provider login link, not the authentication code. Return to the Claude page and copy the full code it shows."
  }

  const parts = code.split("#")
  if (parts.length !== 2 || parts.some((part) => !part)) {
    return "Paste the complete authentication code from Claude, including the # character and everything after it."
  }

  return ""
}

export function modelEffortState(efforts, defaultEffort, currentEffort) {
  const supported = Array.from(new Set(Array.isArray(efforts) ? efforts : []))
  if (supported.length === 0) return { efforts: [], selected: "" }
  if (supported.includes(currentEffort)) return { efforts: supported, selected: currentEffort }
  if (supported.includes(defaultEffort)) return { efforts: supported, selected: defaultEffort }
  return { efforts: supported, selected: supported[0] }
}

export function effortLabel(effort) {
  const value = String(effort || "")
  return EFFORT_LABELS[value] || value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export function modelCatalogRefreshAction(refresh) {
  if (refresh?.open) return "poll"
  if (refresh?.applied) return "reload"
  return "stop"
}

function selectedModelConfiguration(modelSelect) {
  const option = modelSelect?.selectedOptions?.[0]
  if (!option) return { efforts: [], defaultEffort: "", description: "" }

  let efforts = []
  try {
    efforts = JSON.parse(option.dataset.efforts || "[]")
  } catch {
    efforts = []
  }
  return {
    efforts,
    defaultEffort: option.dataset.defaultEffort || "",
    description: option.dataset.description || ""
  }
}

function renderProviderSettings(form) {
  const modelSelect = form.querySelector("[data-agent-model-select]")
  const effortSelect = form.querySelector("[data-agent-effort-select]")
  const description = form.querySelector("[data-agent-model-description]")
  if (!modelSelect || !effortSelect) return

  const configuration = selectedModelConfiguration(modelSelect)
  const state = modelEffortState(configuration.efforts, configuration.defaultEffort, effortSelect.value)
  effortSelect.replaceChildren()
  if (state.efforts.length === 0) {
    const option = document.createElement("option")
    option.value = ""
    option.textContent = "Not supported"
    effortSelect.append(option)
  } else {
    state.efforts.forEach((effort) => {
      const option = document.createElement("option")
      option.value = effort
      option.textContent = effortLabel(effort)
      effortSelect.append(option)
    })
  }
  effortSelect.value = state.selected
  if (description) description.textContent = configuration.description
}

function mountProviderSettings(root) {
  root.querySelectorAll("[data-agent-provider-settings]").forEach((form) => {
    if (form.dataset.settingsMounted === "true") return

    form.dataset.settingsMounted = "true"
    const modelSelect = form.querySelector("[data-agent-model-select]")
    modelSelect?.addEventListener("change", () => renderProviderSettings(form))
    renderProviderSettings(form)
  })
}

function setText(root, selector, value) {
  root.querySelectorAll(selector).forEach((element) => {
    element.textContent = value
  })
}

function setHidden(element, hidden) {
  if (!element) return
  element.hidden = hidden
}

function renderSetup(panel, setup) {
  if (!setup || !setup.run_id) return

  const providerName = panel.dataset.providerName || statusLabel(setup.provider)
  const viewState = setupViewState(setup, providerName)
  const authorization = panel.querySelector("[data-setup-authorization]")
  const authorizationLink = panel.querySelector("[data-setup-link]")
  const authorizationCopy = panel.querySelector("[data-setup-authorization-copy]")
  const codeField = panel.querySelector("[data-setup-code-field]")
  const codeInput = panel.querySelector("[data-setup-code]")
  const authCodeForm = panel.querySelector("[data-setup-auth-code-form]")
  const authCodeRunInput = authCodeForm?.querySelector("input[name='setup_run_id']")
  const authCodeError = panel.querySelector("[data-setup-auth-code-error]")
  const waitingAction = panel.querySelector("[data-setup-waiting-action]")
  const failureRow = panel.querySelector("[data-setup-failure-row]")
  const failure = panel.querySelector("[data-setup-failure]")

  setText(panel, "[data-setup-title]", viewState.title)
  setText(panel, "[data-setup-copy]", viewState.copy)
  setText(panel, "[data-setup-status-badge]", viewState.statusLabel)
  setText(panel, "[data-setup-status-value]", viewState.statusLabel)
  setText(panel, "[data-setup-updated-at]", setup.updated_at || "-")

  if (setup.authorization_uri) {
    setHidden(authorization, false)
    setHidden(waitingAction, true)
    authorizationLink.href = setup.authorization_uri
    if (authorizationCopy) authorizationCopy.textContent = setupAuthorizationCopy(setup)
    setHidden(authCodeForm, !setup.requires_authorization_code)
    if (authCodeRunInput) authCodeRunInput.value = setup.run_id
  } else {
    setHidden(authorization, true)
    setHidden(authCodeForm, true)
    setHidden(waitingAction, setup.authorization_code_submitted_at || !OPEN_STATUSES.has(setup.status))
  }

  if (authCodeError) {
    authCodeError.textContent = setup.authorization_code_error || ""
    setHidden(authCodeError, !setup.authorization_code_error)
  }

  if (setup.user_code) {
    setHidden(codeField, false)
    codeInput.value = setup.user_code
  } else {
    setHidden(codeField, true)
  }

  if (setup.failure_message || setup.failure_code) {
    setHidden(failureRow, false)
    failure.textContent = setup.failure_message || setup.failure_code
  } else {
    setHidden(failureRow, true)
  }

  const steps = setupStepStates(setup, setup.provider)
  panel.querySelectorAll("[data-setup-step]").forEach((element) => {
    const state = steps[element.dataset.setupStep] || "upcoming"
    element.classList.toggle("is-done", state === "done")
    element.classList.toggle("is-active", state === "active")
    element.classList.toggle("is-upcoming", state === "upcoming")
  })

  const phase = setupPhase(setup)
  Array.from(panel.classList)
    .filter((className) => className.startsWith("is-phase-"))
    .forEach((className) => panel.classList.remove(className))
  panel.classList.add(`is-phase-${phase}`)
}

function pollSetup(panel) {
  fetch(panel.dataset.statusUrl, { headers: { Accept: "application/json" } })
    .then((response) => {
      if (!response.ok) throw new Error(`Credential setup status failed: ${response.status}`)
      return response.json()
    })
    .then((payload) => {
      panel.dataset.pollFailures = "0"
      const pollError = panel.querySelector("[data-setup-poll-error]")
      setHidden(pollError, true)
      const setup = payload.setup || {}
      renderSetup(panel, setup)

      if (setup.terminal) {
        window.setTimeout(() => window.location.reload(), 1500)
        return
      }

      window.setTimeout(() => pollSetup(panel), 2500)
    })
    .catch(() => {
      const failures = Number(panel.dataset.pollFailures || "0") + 1
      panel.dataset.pollFailures = String(failures)
      const pollError = panel.querySelector("[data-setup-poll-error]")
      if (pollError) {
        pollError.textContent = failures > 1 ? "We cannot check the login status right now. Refresh the page or wait while we retry." : "Connection interrupted. Retrying the login status check."
        setHidden(pollError, false)
      }
      window.setTimeout(() => pollSetup(panel), 5000)
    })
}

function pollModelCatalogRefresh(page) {
  fetch(page.dataset.modelCatalogRefreshStatusUrl, { headers: { Accept: "application/json" } })
    .then((response) => {
      if (!response.ok) throw new Error(`Model catalog refresh status failed: ${response.status}`)
      return response.json()
    })
    .then((payload) => {
      switch (modelCatalogRefreshAction(payload.catalog_refresh)) {
        case "poll":
          window.setTimeout(() => pollModelCatalogRefresh(page), 2500)
          break
        case "reload":
          window.location.reload()
          break
      }
    })
    .catch(() => {
      window.setTimeout(() => pollModelCatalogRefresh(page), 5000)
    })
}

function mountModelCatalogRefresh(root) {
  root.querySelectorAll("[data-agent-model-catalog-refresh='true']").forEach((page) => {
    if (page.dataset.modelCatalogRefreshMounted === "true") return

    page.dataset.modelCatalogRefreshMounted = "true"
    window.setTimeout(() => pollModelCatalogRefresh(page), 800)
  })
}

export function mountAgentCredentialSetup(root = document) {
  mountProviderSettings(root)
  mountModelCatalogRefresh(root)
  root.querySelectorAll("[data-agent-credential-setup]").forEach((panel) => {
    const copyButton = panel.querySelector("[data-setup-code-copy]")
    if (copyButton && copyButton.dataset.copyMounted !== "true") {
      copyButton.dataset.copyMounted = "true"
      copyButton.addEventListener("click", () => {
        const input = panel.querySelector("input[data-setup-code]")
        const value = input?.value || ""
        if (!value) return

        const markCopied = () => {
          copyButton.textContent = "Copied"
          window.setTimeout(() => {
            copyButton.textContent = "Copy"
          }, 1500)
        }
        const selectCode = () => {
          input.focus()
          input.select()
        }

        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(value).then(markCopied, selectCode)
        } else {
          selectCode()
        }
      })
    }

    const authCodeForm = panel.querySelector("[data-setup-auth-code-form]")
    if (authCodeForm && authCodeForm.dataset.validationMounted !== "true") {
      authCodeForm.dataset.validationMounted = "true"
      authCodeForm.addEventListener("submit", (event) => {
        const input = authCodeForm.querySelector("input[name='authorization_code']")
        const message = authorizationCodeValidationMessage(input?.value)
        if (!message) return

        event.preventDefault()
        const error = panel.querySelector("[data-setup-auth-code-error]")
        if (error) {
          error.textContent = message
          setHidden(error, false)
        }
        input?.focus()
      })
    }

    if (panel.dataset.setupOpen !== "true" || panel.dataset.setupTerminal === "true") return

    window.setTimeout(() => pollSetup(panel), 800)
  })
}
